import { deleteMessaje, getLessonItemMessages, postMessaje, putMessaje } from 'core/crud/lesson.crud';
import { useUser } from 'core/lite/hooks/useUser';
import React from 'react';
import { useToasts } from 'react-toast-notifications';
import { parseComment } from './materialComments.utils';

function useMaterialComments({ courseGuid, courseMessageGuid, isReadOnly, t }) {
  const { addToast } = useToasts();
  const { user: currentUser } = useUser();
  const [comments, setComments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (courseMessageGuid) {
      getMessages();
    } else if (isReadOnly) {
      setLoading(false);
    }
  }, [courseMessageGuid, isReadOnly]);

  async function getMessages() {
    try {
      setLoading(true);
      const response = await getLessonItemMessages({ courseGuid, courseMessageGuid });
      const data = response.data.data.comments.map((comment) => parseComment(comment));

      setComments(data);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }

  async function onPublishComment(parentGuid, text) {
    try {
      setLoading(true);
      const _postMessaje = await postMessaje({
        courseGuid,
        data: { message: text, message_for: 'all', parent_guid: parentGuid },
      });

      if (_postMessaje.status == 200) {
        setComments([parseComment({ ..._postMessaje.data.data, user: currentUser }), ...comments]);
      } else {
        addToast(t('lesson:Could not add comment'), { appearance: 'error', autoDismiss: true });
      }

      setLoading(false);
    } catch {
      setLoading(false);
      addToast(t('lesson:Could not add comment'), { appearance: 'error', autoDismiss: true });
    }
  }

  async function onDeleteComment(guid) {
    try {
      setLoading(true);
      const _messaje = await deleteMessaje({ courseGuid: courseGuid, data: { guid: [guid] } });

      if (_messaje.status == 200) {
        setComments(comments.filter((comment) => comment.guid != guid));
        addToast(t('lesson:Comment removed successfully'), { appearance: 'success', autoDismiss: true });
      } else {
        addToast(t('lesson:Could not delete comment'), { appearance: 'error', autoDismiss: true });
      }

      setLoading(false);
    } catch {
      setLoading(false);
      addToast(t('lesson:Could not delete comment'), { appearance: 'error', autoDismiss: true });
    }
  }

  async function onEditComment(guid, value) {
    try {
      setLoading(true);
      const _messaje = await putMessaje({ courseGuid: courseGuid, messajeGuid: guid, data: { message: value } });

      if (_messaje.status == 200) {
        setComments(comments.map((comment) => (comment.guid == guid ? { ...comment, text: value } : comment)));
        addToast(t('lesson:Comment successfully modified'), { appearance: 'success', autoDismiss: true });
      } else {
        addToast(t('lesson:Could not modify comment'), { appearance: 'error', autoDismiss: true });
      }

      setLoading(false);
    } catch {
      setLoading(false);
      addToast(t('lesson:Could not modify comment'), { appearance: 'error', autoDismiss: true });
    }
  }

  return { comments, currentUser, loading, onDeleteComment, onEditComment, onPublishComment };
}

export default useMaterialComments;
